/**
 * Pure decision logic for the QA-only "unlimited, unranked preview run"
 * of a QA-loaded Daily Challenge date - no Phaser dependency, so it's
 * directly unit-testable (see `src/daily/__tests__/qaPreview.test.ts`)
 * without a Scene. `DailyChallengeScene`, `GameScene`, and
 * `DailyResultScene` all resolve through these same functions, so the
 * client and the tests can never drift apart on what counts as "QA
 * preview mode."
 *
 * The bug this exists to prevent: a QA-previewed date's own participation
 * status (or, worse, today's real status) briefly rendering as a normal
 * ranked "N of 3" attempts count, with START ATTEMPT then silently
 * reserving *today's* real (possibly already-exhausted) attempt instead
 * of doing anything with the QA-previewed date. None of the functions
 * below ever take, read, or derive an attempts-remaining count at all -
 * a QA preview has no such concept, so there is nothing to fabricate and
 * nothing that can ever run out.
 */

import { isDailyQaAllowedHost } from "./dailyQa";
import type { DailyGameSceneContext } from "./DailyRunContext";
import type { DailyChallengeSpecAny } from "./types";

/**
 * Whether `DailyChallengeScene` should show the QA-preview screen
 * (skipping the real attempts-status fetch entirely) instead of the
 * normal loading-status/ready/no-attempts flow.
 *
 * Requires BOTH `isQaOverrideActive` (only ever set from the server's own
 * `isQaOverride` response field - the server's own host check in
 * `netlify/functions/daily-challenge.ts` already had to pass for that to
 * be true) AND an independent client-side re-check of the current
 * hostname. That second check is what keeps this unreachable on a
 * production host even through crafted local state: nothing in this
 * codebase ever sets `isQaOverrideActive` from `localStorage` or any
 * other client-writable source, but even if some future change did, a
 * production hostname alone still can't activate this path.
 */
export function shouldEnterQaPreview(isQaOverrideActive: boolean, hostname: string | null | undefined): boolean {
  return isQaOverrideActive && isDailyQaAllowedHost(hostname);
}

/**
 * Whether PLAY QA PREVIEW / REPLAY QA PREVIEW may start a run right now.
 * Takes no attempts-remaining input at all, real or simulated - unlike
 * `canStartAttempt` (the real-reservation gate), there is no counter here
 * to check, so a real, currently-exhausted attempt count can never block
 * this, and calling it any number of times in a row for the same
 * `(hasSpec, isQaOverrideActive, hostname)` always returns the same
 * answer: QA previews are unlimited by construction, not by a counter
 * that merely never happens to hit zero.
 */
export function canStartQaPreview(hasSpec: boolean, isQaOverrideActive: boolean, hostname: string | null | undefined): boolean {
  return hasSpec && shouldEnterQaPreview(isQaOverrideActive, hostname);
}

/**
 * The `DailyGameSceneContext` for a QA preview run. `attemptNumber`/
 * `attemptsRemaining` are inert placeholders - see their doc on
 * `DailyGameSceneContext` - never read meaningfully by anything gated on
 * `isQaPreview: true`. Deterministic and side-effect-free: never touches
 * `DailyChallengeClient` (no `daily-attempt` call), `DailyLocalState`, or
 * any other real/persisted state.
 */
export function buildQaPreviewContext(spec: DailyChallengeSpecAny): DailyGameSceneContext {
  return { spec, attemptNumber: 1, attemptsRemaining: 0, isQaPreview: true };
}

/**
 * Whether a Daily attempt's outcome may be submitted via
 * `DailyChallengeClient.submitResult()` (`daily-result`) - never for a
 * QA preview, on any path: `DailyResultScene.create()`'s normal
 * completion flow and `GameScene`'s teardown-DNF fallback (an abandoned
 * mid-run exit) both gate on this same function, so neither can ever
 * write a real result, participation row, best-score, rank, or
 * leaderboard entry for a QA preview run, however it ends.
 */
export function shouldSubmitDailyResult(isQaPreview: boolean | undefined): boolean {
  return !isQaPreview;
}
